import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import projects from '../data/projects';
import './HomePage.css';

const gridVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.2 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 80 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1, ease: [0.77, 0, 0.175, 1] },
  },
};

function ProjectCard({ project, index }) {
  return (
    <motion.div variants={cardVariants}>
      <Link to={`/portfolio/${project.slug}`} className="project-card">
        <div className="project-card-image">
          <img
            src={project.coverImage}
            alt={project.title}
            loading={index > 1 ? 'lazy' : 'eager'}
          />
          <div className="project-card-gradient" />
          <div className="project-card-hover-content">
            <span className="project-card-hover-location">{project.location}</span>
            <span className="project-card-view">View Project</span>
          </div>
        </div>
        <div className="project-card-info">
          <h3>{project.title}</h3>
          <span className="project-card-divider" />
          <p>{project.location}</p>
        </div>
      </Link>
    </motion.div>
  );
}

function HomePage() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);
  const heroScale = useTransform(scrollY, [0, 800], [1.05, 1.15]);
  const heroTextY = useTransform(scrollY, [0, 600], [0, -80]);
  const heroTextOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  const [gridRef, gridInView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [ctaRef, ctaInView] = useInView({ triggerOnce: true, threshold: 0.3 });

  return (
    <motion.div
      className="home-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="home-hero">
        <div className="home-hero-image">
          <motion.img
            src="/images/homepage/IMG_3595.png"
            alt="Casa Primas — painting and Italian pottery on bamboo table"
            style={{ y: heroY, scale: heroScale }}
          />
        </div>
        <div className="home-hero-overlay" />
        <motion.div
          className="home-hero-content"
          style={{ y: heroTextY, opacity: heroTextOpacity }}
        >
          <motion.span
            className="home-hero-label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
          >
            Interior Design & Home Organization
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.77, 0, 0.175, 1] }}
          >
            Casa Primas
          </motion.h1>
          <motion.p
            className="home-hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.4, 0, 0.2, 1] }}
          >
            Creating spaces that tell your story
          </motion.p>
          <motion.div
            className="home-hero-scroll"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
          >
            <span>Scroll</span>
            <div className="scroll-line" />
          </motion.div>
        </motion.div>
      </section>

      {/* Selected Work */}
      <section className="home-portfolio">
        <motion.div
          className="home-portfolio-header"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        >
          <span className="section-label">Selected Work</span>
          <h2>Featured Projects</h2>
          <p className="home-portfolio-subtitle">
            Every home has a story. We help you tell it beautifully.
          </p>
        </motion.div>

        <motion.div
          className="project-grid"
          ref={gridRef}
          variants={gridVariants}
          initial="hidden"
          animate={gridInView ? 'visible' : 'hidden'}
        >
          {projects.map((project, index) => (
            <ProjectCard key={project.slug} project={project} index={index} />
          ))}
        </motion.div>
      </section>

      {/* Contact CTA */}
      <section className="home-cta" ref={ctaRef}>
        <motion.div
          className="home-cta-line"
          initial={{ scaleX: 0 }}
          animate={ctaInView ? { scaleX: 1 } : {}}
          transition={{ duration: 1.2, ease: [0.77, 0, 0.175, 1] }}
        />
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={ctaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          Let's Create Something Beautiful
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={ctaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
        >
          Ready to transform your space? We'd love to hear from you.
        </motion.p>
        <motion.a
          href="mailto:hello@casaprimas.com"
          className="cta-button"
          initial={{ opacity: 0, y: 20 }}
          animate={ctaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.7, ease: [0.4, 0, 0.2, 1] }}
        >
          Get in Touch
        </motion.a>
      </section>
    </motion.div>
  );
}

export default HomePage;
